import type React from 'react'
import { Area, AreaChart, CartesianGrid, Dot, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import styled from 'styled-components'
import type { CPModelEstimate, CPReference, CriticalPowerPoint } from '../../hooks/usePowerStats'
import { theme } from '../../theme/theme'

interface CriticalPowerChartProps {
  criticalPower: CriticalPowerPoint[]
  cpModel: CPModelEstimate
  cpReference?: CPReference | null
}

const Container = styled.div`
  width: 100%;

  /* Remove blue outline on chart elements when clicked */
  .recharts-surface {
    outline: none;
  }

  .recharts-wrapper {
    outline: none;
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const StatCard = styled.div<{ $isPersonalBest?: boolean }>`
  border: 2px solid ${({ theme, $isPersonalBest }) => ($isPersonalBest ? theme.colors.primary : theme.colors.surface)};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xs};
  text-align: center;
  position: relative;

  @media (max-width: 640px) {
    padding: ${theme.spacing.xs};
  }
`

const StatValue = styled.div<{ $isPersonalBest?: boolean }>`
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};

  @media (max-width: 640px) {
    font-size: ${theme.typography.md};
  }
`

const StatLabel = styled.div<{ $isPersonalBest?: boolean }>`
  font-size: ${theme.typography.xs};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`

const StatsPersonalBest = styled.div<{ $isPersonalBest?: boolean }>`
  font-size: ${theme.typography.xs};
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
  margin-top: ${theme.spacing.xs};
`

// Custom tooltip component
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    const currentPower = data.power
    const referencePower = data.referencePower
    const percentage = referencePower && referencePower > 0 ? Math.round((currentPower / referencePower) * 100) : null

    return (
      <div
        style={{
          background: theme.colors.cardBackground,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borderRadius.sm,
          padding: theme.spacing.sm,
          fontSize: theme.typography.sm,
          color: theme.colors.text,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: theme.spacing.xs }}>
          {data.label} {data.isKey ? `– ${data.description}` : 'Peak Power'}
        </div>
        <div style={{ color: '#F59E0B', fontWeight: 600, marginBottom: '4px' }}>Current: {currentPower} W</div>
        {referencePower && (
          <>
            <div style={{ color: '#8B5CF6', fontWeight: 600, marginBottom: '4px' }}>
              8-week best: {referencePower} W
            </div>
            {percentage !== null && (
              <div style={{ color: theme.colors.textSecondary, fontSize: theme.typography.xs }}>
                {percentage}% of 8-week best
              </div>
            )}
          </>
        )}
      </div>
    )
  }
  return null
}

export const CriticalPowerChart: React.FC<CriticalPowerChartProps> = ({ criticalPower, cpModel, cpReference }) => {
  if (!criticalPower || criticalPower.length === 0) {
    return null
  }

  // Merge critical power data with reference data
  const mergedData = criticalPower.map((cp) => {
    const reference = cpReference?.values.find((ref) => ref.duration === cp.duration)
    return {
      ...cp,
      referencePower: reference?.power || null,
    }
  })

  // Find max power for Y-axis domain (including reference data)
  const maxCurrentPower = Math.max(...criticalPower.map((cp) => cp.power))
  const maxReferencePower = cpReference ? Math.max(...cpReference.values.map((ref) => ref.power)) : 0
  const maxPower = Math.max(maxCurrentPower, maxReferencePower)
  const yAxisMax = Math.ceil(maxPower / 25) * 25 // Round up to nearest 25

  const minCurrentPower = Math.min(...criticalPower.map((cp) => cp.power))
  const minReferencePower = cpReference ? Math.min(...cpReference.values.map((ref) => ref.power)) : Number.MAX_VALUE
  const minPower = Math.min(
    minCurrentPower,
    minReferencePower === Number.MAX_VALUE ? minCurrentPower : minReferencePower
  )
  const yAxisMin = Math.max(Math.floor(minPower / 25) * 25 - 50, 0) // min - 50W - Rounded up to the nearest 25

  return (
    <Container>
      <ResponsiveContainer width="100%" height={500}>
        <AreaChart data={mergedData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <defs>
            <linearGradient id="powerGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.8} />
              <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#E5E7EB99" />
          <XAxis
            dataKey="duration"
            type="number"
            scale="log"
            domain={['dataMin', 'dataMax']}
            stroke={theme.colors.textSecondary}
            tick={{ fill: theme.colors.textSecondary, fontSize: 12 }}
            tickFormatter={(value) => {
              const point = mergedData.find((cp) => cp.duration === value)
              return point ? point.label : ''
            }}
            ticks={mergedData.map((cp) => cp.duration)}
            label={{
              value: 'Duration',
              position: 'insideBottom',
              offset: -10,
              fill: theme.colors.text,
            }}
          />
          <YAxis
            stroke={theme.colors.textSecondary}
            tick={{ fill: theme.colors.textSecondary, fontSize: 12 }}
            label={{
              value: 'Power (W)',
              angle: -90,
              position: 'insideLeft',
              fill: theme.colors.text,
            }}
            domain={[yAxisMin, yAxisMax]}
          />
          <Tooltip content={<CustomTooltip />} />
          {/* Critical Power (CP) reference line
          <ReferenceLine
            y={cpModel.cp}
            stroke="#CCCCCC"
            strokeWidth={2}
            strokeDasharray="2 12"
            label={{
              value: `CP: ${cpModel.cp}W`,
              position: 'left',
              fill: '#CCCCCC',
              fontSize: 12,
              fontWeight: 600,
            }}
          />
           */}
          {/* Reference curve - N-week best */}
          {cpReference?.values && (
            <Line
              type="monotone"
              dataKey="referencePower"
              stroke="#8B5CF6"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              name="8-week best"
            />
          )}
          {/* Current workout power curve */}
          <Area
            type="monotone"
            dataKey="power"
            stroke="#F59E0B"
            strokeWidth={3}
            fill="url(#powerGradient)"
            dot={(props: any) => {
              const point = mergedData[props.index]
              const radius = point?.isKey ? 6 : 2
              const strokeWidth = point?.isKey ? 2 : 1
              return <Dot {...props} r={radius} fill="#F59E0B" stroke="#fff" strokeWidth={strokeWidth} />
            }}
            activeDot={(props: any) => {
              const point = mergedData[props.index]
              const radius = point?.isKey ? 8 : 5
              return <Dot {...props} r={radius} fill="#F59E0B" stroke="#fff" strokeWidth={2} />
            }}
          />
        </AreaChart>
      </ResponsiveContainer>

      <StatsGrid>
        {mergedData
          .filter((cp) => cp.isKey)
          .map((cp) => {
            const isPersonalBest = !!(cp.referencePower && cp.power > cp.referencePower)
            return (
              <StatCard key={cp.label} $isPersonalBest={isPersonalBest}>
                <StatValue $isPersonalBest={isPersonalBest}>{cp.power}W</StatValue>
                <StatLabel $isPersonalBest={isPersonalBest}>
                  {cp.label} – {cp.description}
                </StatLabel>
                {isPersonalBest && (
                  <StatsPersonalBest $isPersonalBest={isPersonalBest}>New 8-week best!</StatsPersonalBest>
                )}
              </StatCard>
            )
          })}

        {/*<StatCard key="cp" $isPersonalBest={false}>
          <StatValue $isPersonalBest={false}>{cpModel.cp}W</StatValue>
          <StatLabel $isPersonalBest={false}>CP – Critical Power</StatLabel>
        </StatCard>*/}
      </StatsGrid>
      <StatsGrid>
        {/*
        <StatCard key="CP">
          <StatValue>{cpModel.cp} W</StatValue>
          <StatLabel>Critical Power (CP)</StatLabel>
        </StatCard>
        <StatCard key="WP">
          <StatValue>{cpModel.wPrime} kJ</StatValue>
          <StatLabel>Anaerobic work capacity (W')</StatLabel>
        </StatCard>
        */}
      </StatsGrid>
    </Container>
  )
}
